// "What is this game?" card — opened from the ℹ️ glyph in the menu's top-right corner.
//
// The menu shows a Play button, three settings pills and a progress bar, but never says what the
// game IS: smash the right words, collect stars, travel from planet to planet. A parent opening the
// app for the first time (and a child who cannot yet read a store listing) has nowhere to find
// that. This card is that one place.
//
// It is the same card the web buy prompt and the rating ask use (showPromoCard), so the menu has
// one card style rather than a second one just for this.
//
// READ ALOUD on open, like the in-round help panel (HelpScene): the audience is a beginning reader,
// so a block of small type they cannot decode would be no explanation at all. The 🔊 button replays
// it — unlike HelpScene, which is one tap away on the board, this card sits behind a corner glyph,
// and a child who missed a sentence should not have to find that glyph again. The button is left
// out when the voice readout is muted, so it never looks broken.
import Phaser from 'phaser';
import i18next from 'i18next';
import { showPromoCard, type PromoCardAction } from '@/game/ui/promoCard';
import { speakExplanation, cancelRulePrompt } from '@/game/audio';
import { getVoiceMuted } from '@/game/storage';

/** One card at a time: the glyph stays tappable while the card is up, so guard against a stack. */
let open = false;
/**
 * When the last card went away. A tap on the ℹ️ glyph while the card is up is an outside tap, so it
 * closes the card — and would then be seen by the glyph itself and open a fresh one. The short
 * window makes that gesture read as the toggle the player meant.
 */
let closedAt = 0;

export function showInfoCard(scene: Phaser.Scene): void {
  if (open || Date.now() - closedAt < 300) return;
  open = true;

  const title = i18next.t('info.title');
  const body = i18next.t('info.body');
  // Blank lines separate paragraphs in the locale string; the card renders one line each (and
  // gives the first the star colour — it is the pitch).
  const lines = body.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);

  const speak = (): void => speakExplanation(`${title}. ${body}`, i18next.language);

  const actions: PromoCardAction[] = [];
  if (!getVoiceMuted()) {
    actions.push({
      label: `🔊 ${i18next.t('info.replay')}`,
      kind: 'ghost',
      keepOpen: true,
      // Restart from the top rather than queueing a second readout behind the first.
      onClick: () => { cancelRulePrompt(); speak(); },
    });
  }
  actions.push({ label: i18next.t('nav.ok'), kind: 'primary' });

  showPromoCard(scene, { title, lines, actions }, {
    // Tapping away from a panel you opened is how you put it down — and it is the only dismissal a
    // child who cannot read "OK" will find on their own.
    closeOnOutsideTap: true,
    // Every route out lands here: the OK button, a tap outside, leaving the menu for a round. A
    // voice still explaining the card would otherwise talk over whatever comes next.
    onDismiss: () => { cancelRulePrompt(); open = false; closedAt = Date.now(); },
  });
  speak();
}
